'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useSubscriptionValidation } from '@/hooks/useSubscriptionValidation';
import { useAuth } from '@/hooks/useAuth';

interface SubscriptionValidationWrapperProps {
  children: React.ReactNode;
  allowedPaths?: string[];
}

export function SubscriptionValidationWrapper({ 
  children, 
  allowedPaths = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/subscription-expired', 
    '/subscription/admin-renewal', 
    '/admin/school-subscriptions/create',
    '/admin/subscription-plans',
    '/admin/payments',
    '/admin/course-payments',
    '/admin/coupons',
    '/auth/login', 
    '/auth/register',
    '/auth/logout',
    '/logout'
  ] 
}: SubscriptionValidationWrapperProps) {
  const { user } = useAuth();
  const router = useRouter();
  const { 
    subscriptionStatus, 
    loading, 
    isSubscriptionValid, 
    shouldRedirect, 
    redirectPath 
  } = useSubscriptionValidation();

  // Check if current path is allowed
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAllowedPath = allowedPaths.some(path => currentPath.startsWith(path));

  // Handle automatic redirection - must be before any conditional returns
  useEffect(() => {
    // Skip auto-redirect for admin; handled by useAdminSubscriptionGate on restricted pages only
    if (user?.roles?.includes('admin')) return;
    if (shouldRedirect && redirectPath && !isAllowedPath) {
      // console.log('SubscriptionValidationWrapper: Redirecting to', redirectPath);
      router.replace(redirectPath);
    }
  }, [user, shouldRedirect, redirectPath, isAllowedPath, router]);

  // Check if user belongs to super admin school
  const superAdminSchoolId = process.env.NEXT_PUBLIC_SUPER_ADMIN_SCHOOL_ID || 'cmljhuey3000sqxxe6ew0btq9';
  const isFromSuperAdminSchool = user?.schoolId === superAdminSchoolId;
  
  // Check if user has roles that should be excluded from subscription checks when from super admin school
  const excludedRoles = ['admin', 'student', 'instructor', 'support_admin'];
  const hasExcludedRole = user?.roles?.some(role => excludedRoles.includes(role));
  const shouldSkipCheck = isFromSuperAdminSchool && hasExcludedRole;

  // Skip validation for super_admin and support_super_admin roles
  // Also skip for users from super admin school with excluded roles
  if (
    user?.roles?.includes('super_admin') || 
    user?.roles?.includes('support_super_admin') ||
    shouldSkipCheck
  ) {
    return <>{children}</>;
  }

  // For admin role, we still need to check their subscription
  // The validation will be handled by the useSubscriptionValidation hook

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-lg text-gray-600">Checking subscription status...</span>
        </div>
      </div>
    );
  }

  // Debug logging
  // console.log('SubscriptionValidationWrapper state:', {
  //   loading,
  //   isSubscriptionValid,
  //   shouldRedirect,
  //   redirectPath,
  //   currentPath,
  //   isAllowedPath,
  //   subscriptionStatus
  // });

  // If subscription is invalid and we're not on an allowed path, show loading while redirecting
  if (shouldRedirect && !isAllowedPath && redirectPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Redirecting...</p>
          {subscriptionStatus?.message && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              {subscriptionStatus.message}
            </p>
          )}
        </div>
      </div>
    );
  }

  // If subscription is valid, render children
  if (isSubscriptionValid) {
    return <>{children}</>;
  }

  // If we're on an allowed path, render children (but still show redirect if needed)
  if (isAllowedPath) {
    return <>{children}</>;
  }

  // Fallback - should not reach here due to redirect logic
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Subscription Issue</h2>
        <p className="text-gray-600">There is an issue with your subscription. Please contact support.</p>
      </div>
    </div>
  );
}
